import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ITEM_DATE_KEYS, shortUuid, pathOf, type FileLocation, type Item, type ItemPhoto, type ItemContainer, type ItemCategory } from "@egalregal/shared";
import { log } from "../../logger";
import { requests } from "../../shared/api";
import { useAppStore, readableContainers } from "../../shared/store/app";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { readHistoryIndex, resolveBackTarget } from "../shell/back";
import { ContentHeader } from "../shell/ContentHeader";
import { useShell } from "../shell/useShell";
import { useT } from "../../shared/i18n";
import { renderMarkdown } from "../../shared/lib/markdown";
import { DeleteOutlineIcon, RotateRightIcon, AddIcon, RemoveIcon } from "../../shared/components/icons";
import { CategoryChip } from "../../shared/components/CategoryChip";
import { CategoryAutocomplete } from "../../shared/components/CategoryAutocomplete";
import { PhotosSection } from "./PhotosSection";
import { ItemPhotoLightbox } from "./ItemPhotoLightbox";
import { ContainersSection } from "./ContainersSection";
import { DatePropertyChips } from "../../shared/components/Deadlines";
import { parseLabelUuid } from "../scan/label";
import { placementFailureNotice } from "./placementNotice";
import { usePlacementsStore } from "./placementsStore";
import { canWriteContainer } from "../catalogs/access";
import { useDemoMode } from "../demo/useDemoMode";
import {
  DatePropertyFields,
  dateValuesOf,
  mergeDateProperties,
  type DatePropertyValues,
} from "../../shared/components/DatePropertyFields";

interface EditRow {
  key: string;
  value: string;
  /** The stored value before editing, so untouched rows round-trip with their
   *  original type (a number stays a number instead of becoming a string). */
  original: unknown;
}

export function ItemDetailView() {
  const t = useT();
  const demoMode = useDemoMode();
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const openContainer = useOpenContainer();
  const shell = useShell();
  const setTotal = usePlacementsStore((state) => state.setTotal);
  const tree = useAppStore((s) => s.containers);
  const containerPool = useAppStore(readableContainers);
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);

  const [item, setItem] = useState<Item | null>(null);
  const [containers, setContainers] = useState<ItemContainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeSeverity, setNoticeSeverity] = useState<"success" | "error">("success");

  // Single-placement quantity editor (scan-followups 04): the write is
  // immediate, so the row only needs a saving lock and the inline error.
  const [quantitySaving, setQuantitySaving] = useState(false);
  const [quantityError, setQuantityError] = useState<string | null>(null);

  // A refusal belongs to the item it was raised for; do not carry it over.
  useEffect(() => {
    setQuantityError(null);
  }, [itemId]);

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState<ItemCategory>("other");
  const [editDescription, setEditDescription] = useState("");
  const [editDescriptionPreview, setEditDescriptionPreview] = useState(false);
  const [editDates, setEditDates] = useState<DatePropertyValues>({});
  const [editRows, setEditRows] = useState<EditRow[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await requests.items.get(itemId);
      setItem(data.item);
      setContainers(data.containers);
    } catch (err) {
      log.error({ err }, `Failed to load item ${shortUuid(itemId)}`);
      setLoadError(t("loadError"));
    } finally {
      setLoading(false);
    }
  }, [itemId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const photos: ItemPhoto[] = item?.photos ?? [];

  // Back follows the contract (spec §6): the owning container when the item has
  // one, otherwise in-app history, otherwise storage. The item's first
  // placement is its owning container.
  const backTarget = resolveBackTarget({
    location,
    historyIndex: readHistoryIndex(),
    owningContainerOf: () => containers[0]?.containerId ?? null,
  });
  const goBack = () => {
    if (backTarget.kind === "history") navigate(-1);
    else navigate(backTarget.to);
  };

  // The item name is the title and the crumb trail ends at the owning container;
  // tapping that crumb opens the container detail (spec §3). This screen renders
  // the shell's shared ContentHeader because only it holds the fetched item —
  // every other route's header is resolved by AppShell.
  const owner = containers[0];
  const ownerPath = owner ? pathOf(tree, owner.containerId) : [];
  const ownerCrumbs = owner
    ? ownerPath.length > 0
      ? ownerPath.map((node) => ({ id: node.id, label: node.name }))
      : [{ id: owner.containerId, label: owner.name }]
    : [];
  const header = {
    title: item?.name ?? t("itemDetail"),
    back: backTarget,
    crumbs: owner ? [{ id: null, label: t("allStorage") }, ...ownerCrumbs] : null,
  };
  const openCrumb = (containerId: string | null) =>
    navigate(containerId ? `/browse/${containerId}` : "/browse");

  const handlePhotoUpload = async (file: File, location?: FileLocation | null) => {
    if (!itemId) return;
    try {
      await requests.items.addPhoto(itemId, file, "general", location);
      await load();
    } catch (err) {
      log.warn({ err }, "Failed to upload item photo");
    }
  };

  const handleRemovePhoto = async (photoId: string) => {
    if (!itemId) return;
    try {
      await requests.items.removePhoto(itemId, photoId);
      await load();
    } catch (err) {
      log.warn({ err }, "Failed to remove item photo");
    }
  };

  const handleReorderPhotos = async (ids: string[]) => {
    if (!itemId) return;
    try {
      await requests.items.reorderPhotos(itemId, ids);
      setNoticeSeverity("success");
      setNotice(t("itemPhotosReordered"));
      await load();
    } catch (err) {
      log.warn({ err }, "Failed to reorder item photos");
      setNoticeSeverity("error");
      setNotice(t("itemReorderPhotoFailed"));
    }
  };

  const handleRefresh = async () => {
    if (!itemId) return;
    setRefreshing(true);
    try {
      await requests.items.refresh([itemId]);
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * The item detail's single-placement stepper (scan-followups 04). Each tap
   * writes the absolute total through the placement ledger (placement-ledger
   * 03) — mirror update now; an unreachable write fails fast and is not queued
   * — and floors at 1 so the count can never unplace the item. A failed write
   * leaves the displayed total alone and shows the shared inline notice.
   */
  const handlePlacementQuantity = async (next: number) => {
    const placement = containers[0];
    if (!item || !placement || quantitySaving) return;
    const target = Math.max(1, next);
    if (target === placement.quantity) return;
    setQuantitySaving(true);
    setQuantityError(null);
    const outcome = await setTotal(placement.containerId, item, target);
    setQuantitySaving(false);
    if (outcome.status !== "saved") {
      log.warn(
        { outcome, itemId: item.id, containerId: placement.containerId },
        "Failed to adjust the item's placement quantity",
      );
      // A failed write is never queued (ADR-0005): the row keeps the stored
      // total and the notice says why the change was not saved.
      setQuantityError(placementFailureNotice(t, outcome));
      return;
    }
    setContainers((current) =>
      current.map((link) =>
        link.containerId === placement.containerId ? { ...link, quantity: outcome.quantity } : link,
      ),
    );
  };

  /**
   * Delete from the edit dialog (item-edit-delete 01). Success closes the edit
   * dialog and lands on the item list; failure reports through the snackbar and
   * keeps the dialog mounted with the in-progress edits for a retry.
   */
  const handleDelete = async () => {
    if (!itemId) return;
    setDeleting(true);
    try {
      await requests.items.delete(itemId);
      setEditOpen(false);
      navigate("/items");
    } catch (err) {
      log.warn({ err, itemId }, "Failed to delete item");
      setNoticeSeverity("error");
      setNotice(
        t("itemDeleteError", {
          error: err instanceof Error ? err.message : String(err),
        }),
      );
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = () => {
    if (!item) return;
    setEditName(item.name);
    setEditCategory(item.category);
    setEditDescription(item.description);
    setEditDescriptionPreview(false);
    setEditDates(dateValuesOf(item.properties));
    setEditRows(
      Object.entries(item.metadata).map(([key, value]) => ({
        key,
        value: typeof value === "string" ? value : JSON.stringify(value),
        original: value,
      })),
    );
    setEditOpen(true);
  };

  const updateEditRow = (idx: number, patch: Partial<EditRow>) =>
    setEditRows((rows) => rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const handleSaveEdit = async () => {
    if (!itemId || !item) return;
    const trimmedName = editName.trim();
    if (!trimmedName) return;
    const metadata: Record<string, unknown> = {};
    for (const row of editRows) {
      if (!row.key.trim()) continue;
      const stringForm = typeof row.original === "string" ? row.original : JSON.stringify(row.original);
      metadata[row.key.trim()] = row.value === stringForm ? row.original : row.value;
    }
    setEditSaving(true);
    try {
      await requests.items.update(itemId, {
        name: trimmedName,
        category: editCategory,
        description: editDescription.trim(),
        metadata,
        properties: mergeDateProperties(item.properties, editDates),
      });
      setNoticeSeverity("success");
      setNotice(t("itemEditSaved"));
      setEditOpen(false);
      await load();
      // A stored edit must be findable again immediately — re-embed the item.
      void useAppStore.getState().refreshItemMirror();
    } catch (err) {
      log.warn({ err }, "Failed to save item details");
      setNoticeSeverity("error");
      setNotice(t("itemEditSaveError"));
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        {/* The header renders before the item lands so the view keeps one title;
            it upgrades from the generic label to the item name. */}
        <ContentHeader
          model={{ ...header, crumbs: null }}
          compact={shell === "phone"}
          onBack={goBack}
          onCrumb={openCrumb}
        />
        <LinearProgress />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (loadError !== null || !item) {
    return (
      <Box>
        <ContentHeader
          model={{ ...header, title: t("itemDetail"), crumbs: null }}
          compact={shell === "phone"}
          onBack={goBack}
          onCrumb={openCrumb}
        />
        <Typography variant="body1" color="error">
          {loadError ?? t("loadError")}
        </Typography>
      </Box>
    );
  }

  const heroPhoto = photos[0];
  const unknownProperties = (properties: Item["properties"]) => {
    const known = new Set<string>(ITEM_DATE_KEYS);
    return Object.entries(properties ?? {}).filter(([key]) => !known.has(key));
  };

  // Exactly one placement may be adjusted in place; several stay the scan
  // surface's selector and none has nothing to count (scan-followups 04). A
  // read-only share keeps the count and drops the stepper.
  const singlePlacement = containers.length === 1 ? containers[0] : undefined;
  const canEditSinglePlacement =
    !demoMode &&
    singlePlacement !== undefined &&
    canWriteContainer(
      containerPool,
      entries,
      sharedWithMe,
      singlePlacement.containerId,
      selectedEntryId,
    );

  return (
    <Box>
      <ContentHeader
        model={header}
        compact={shell === "phone"}
        linkLastCrumb
        onBack={goBack}
        onCrumb={openCrumb}
      />

      {/* Hero photo or placeholder */}
      {heroPhoto ? (
        <Box
          component="img"
          src={requests.media.itemPhotoPreviewUrl(item.id, heroPhoto.id)}
          alt={item.name}
          sx={{ width: "100%", maxHeight: 300, objectFit: "cover", borderRadius: 1, mb: 2 }}
        />
      ) : (
        <Box sx={{ width: "100%", height: 200, bgcolor: "action.hover", borderRadius: 1, mb: 2 }} />
      )}

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
          {item.barcode}
        </Typography>
        {/* Display-only label: a QR tag *is* the item barcode, so a plain-UUID
            barcode reads "QR" while the stored type stays "unknown"
            (qr-item-tags 02). */}
        <Chip
          label={parseLabelUuid(item.barcode) ? t("barcodeTypeQr") : item.barcodeType}
          size="small"
          variant="outlined"
        />
        <CategoryChip category={item.category} />
      </Stack>

      {/* Deadlines and reference date (ADR-0037) — each chip painted by its own
          value; purchased-on stays neutral because it never runs out. */}
      <Box sx={{ mb: 2 }} data-testid="item-date-chips">
        <DatePropertyChips properties={item.properties} />
        {unknownProperties(item.properties).map(([key, property]) => (
          <Typography key={key} variant="caption" color="text.secondary" display="block">
            {key}: {property.value}
          </Typography>
        ))}
      </Box>

      {item.description && (
        <Box component="div" data-testid="item-description-markdown" sx={{ "& p": { my: 1 }, mb: 2 }}>
          {renderMarkdown(item.description)}
        </Box>
      )}

      <PhotosSection
        itemId={item.id}
        itemName={item.name}
        identity={owner ? { uuid: owner.containerId, name: owner.name } : undefined}
        photos={photos}
        onStagedCapture={({ file, location }) => void handlePhotoUpload(file, location)}
        onOpenPhoto={setLightboxIndex}
        onRemovePhoto={(photoId) => void handleRemovePhoto(photoId)}
        onReorder={(ids) => void handleReorderPhotos(ids)}
      />

      {/* Metadata */}
      {Object.keys(item.metadata).length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("itemMetadata")}
          </Typography>
          <List dense disablePadding>
            {Object.entries(item.metadata).map(([key, value]) => (
              <ListItem key={key} disablePadding>
                <ListItemText
                  primary={key}
                  secondary={typeof value === "string" ? value : JSON.stringify(value)}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <ContainersSection
        containers={containers}
        onOpen={(containerId) => void openContainer(containerId)}
        quantity={
          singlePlacement && canEditSinglePlacement
            ? {
                value: singlePlacement.quantity,
                saving: quantitySaving,
                onChange: (next) => void handlePlacementQuantity(next),
              }
            : undefined
        }
        quantityError={quantityError}
      />

      <Divider sx={{ my: 2 }} />

      {/* Actions — Edit and Refresh only; Delete lives on the edit dialog
          (item-edit-delete 01). The read-only demo hides the whole row. */}
      {!demoMode && (
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }} data-testid="item-actions">
          <Button variant="outlined" onClick={openEditDialog}>
            {t("itemEdit")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RotateRightIcon />}
            onClick={() => void handleRefresh()}
            disabled={refreshing}
          >
            {t("itemRefresh")}
          </Button>
        </Stack>
      )}

      <ItemPhotoLightbox
        open={lightboxIndex !== null && photos.length > 0}
        index={lightboxIndex}
        itemId={item.id}
        photos={photos}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />

      <Snackbar
        open={notice !== null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={noticeSeverity} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      </Snackbar>

      {/* Edit item details dialog (ticket 02) */}
      <Dialog
        open={editOpen}
        onClose={() => {
          // A delete already in flight owns the dialog until it settles.
          if (!deleting) setEditOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("itemEditDialogTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t("itemEditName")}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <CategoryAutocomplete
              label={t("filterType")}
              value={editCategory}
              onChange={setEditCategory}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {t("itemEditDescription")}
              </Typography>
              <Button size="small" onClick={() => setEditDescriptionPreview((p) => !p)}>
                {editDescriptionPreview ? t("edit") : t("preview")}
              </Button>
            </Box>
            {editDescriptionPreview ? (
              <Box component="div" data-testid="item-description-preview" sx={{ "& p": { my: 0.5 } }}>
                {renderMarkdown(editDescription)}
              </Box>
            ) : (
              <TextField
                fullWidth
                inputProps={{ "aria-label": t("itemEditDescription") }}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                multiline
                minRows={2}
              />
            )}
            <Typography variant="overline" color="text.secondary">
              {t("itemDates")}
            </Typography>
            <DatePropertyFields values={editDates} onChange={setEditDates} />
            <Typography variant="overline" color="text.secondary">
              {t("itemMetadata")}
            </Typography>
            {editRows.map((row, idx) => (
              <Stack key={idx} spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("itemEditKey")}
                  value={row.key}
                  onChange={(e) => updateEditRow(idx, { key: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t("itemEditValue")}
                  value={row.value}
                  onChange={(e) => updateEditRow(idx, { value: e.target.value })}
                  multiline
                  minRows={2}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    aria-label={t("itemEditRemoveRow")}
                    onClick={() => setEditRows((rows) => rows.filter((_, i) => i !== idx))}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
              </Stack>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => setEditRows((rows) => [...rows, { key: "", value: "", original: "" }])}
              sx={{ alignSelf: "flex-start" }}
            >
              {t("itemEditAddRow")}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving || deleting}>
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!editName.trim() || !editCategory.trim() || editSaving || deleting}
            onClick={() => void handleSaveEdit()}
          >
            {t("save")}
          </Button>
        </DialogActions>

        {/* Destructive section below the edit dialog's actions. The section owns
            the trigger and its confirmation; the parent keeps the delete state
            and the error snackbar (item-edit-delete 01). */}
        <DeleteItemSection deleting={deleting} onDelete={handleDelete} />
      </Dialog>
    </Box>
  );
}

/**
 * The edit dialog's destructive area (item-edit-delete 01). Owns the trigger
 * and the confirmation dialog that gates it, so the confirmation state cannot
 * outlive the section. `onDelete` performs the request in the parent; the
 * confirmation closes once it settles, success or failure.
 */
function DeleteItemSection({
  deleting,
  onDelete,
}: {
  deleting: boolean;
  onDelete: () => Promise<void>;
}) {
  const t = useT();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const confirm = async () => {
    try {
      await onDelete();
    } finally {
      // Failure returns the user to the still-open edit dialog; on success the
      // parent has already closed it and navigated away.
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Box
        data-testid="delete-item-section"
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography component="span" variant="subtitle2" color="error.main">
          {t("itemDelete")}
        </Typography>
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          disabled={deleting}
          onClick={() => setConfirmOpen(true)}
        >
          {t("delete")}
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => {
          // Escape and backdrop clicks must not dismiss a delete in flight.
          if (!deleting) setConfirmOpen(false);
        }}
      >
        <DialogTitle>{t("itemDeleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("itemDeleteConfirmBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            {t("cancel")}
          </Button>
          <Button color="error" onClick={() => void confirm()} disabled={deleting}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
